/**
 * Generic hooks for module-level permission checks
 * Eliminates code duplication across Entities, Properties, and Control Panel
 */

import { useMemo } from 'react';
import { usePermissions } from 'src/context/PermissionsContext';
import { hasAnyPermission, getModuleFromRouteName } from 'src/utils/permission-helper';

/**
 * Get all module-specific permissions in a single hook
 * Returns an object with all permission checks for a given module
 * 
 * @param moduleName - The module name (e.g., 'entities', 'properties', 'control-panel')
 * @returns Object with permission flags
 */
export function useModulePermissions(moduleName?: string) {
  const { permissions } = usePermissions();

  return useMemo(() => {
    if (!moduleName || !Array.isArray(permissions)) {
      return {
        canView: true,
        canEdit: true,
        canDelete: true,
        canViewActions: true,
        canViewDetails: true,
        canEditDetails: true,
        canViewDetailsActions: true,
      };
    }

    const moduleUpper = getModuleFromRouteName(moduleName) || moduleName.toUpperCase();
    
    // Control Panel has different permission structure
    if (moduleName === 'control-panel') {
      return {
        canView: hasAnyPermission(['CONTROL_PANEL:VIEW'], permissions),
        canEdit: hasAnyPermission(['CONTROL_PANEL:EDIT'], permissions),
        canDelete: hasAnyPermission(['CONTROL_PANEL:DELETE'], permissions),
        canViewActions: hasAnyPermission(['CONTROL_PANEL:ACTIONS'], permissions),
        canViewDetails: hasAnyPermission(['CONTROL_PANEL:DETAILS_VIEW'], permissions),
        canEditDetails: hasAnyPermission(['CONTROL_PANEL:DETAILS_EDIT'], permissions),
        canViewDetailsActions: hasAnyPermission(['CONTROL_PANEL:ACTIONS'], permissions), // Control Panel uses ACTIONS for details too
      };
    }

    // Entities and Properties have similar structure
    return {
      canView: hasAnyPermission([`${moduleUpper}:VIEW`], permissions),
      canEdit: hasAnyPermission([`${moduleUpper}:EDIT`], permissions),
      canDelete: hasAnyPermission([`${moduleUpper}:DELETE`], permissions),
      canViewActions: hasAnyPermission([`${moduleUpper}:ACTIONS`], permissions),
      canViewDetails: hasAnyPermission([`${moduleUpper}:DETAILS_VIEW`], permissions),
      canEditDetails: hasAnyPermission([`${moduleUpper}:DETAILS_EDIT`], permissions),
      canViewDetailsActions: hasAnyPermission([`${moduleUpper}:DETAILS_ACTIONS`], permissions),
    };
  }, [moduleName, permissions]);
}



